/**
 * OSS文件管理数据提供者（vuex数据纳入原则：需要异步请求和与其他组件公用的属性和方法都应纳入其中，每个vue单独的不纳入）
 * @author PeterLee
 * @since 2022-07-15
 */
import state from "./FileState";
import {Message, MessageBox} from 'element-ui'
import {getOssFilePage, uploadOssFile, downloadOssFile, copyOssFile, deleteOssFile, deleteBatchOssFile} from "@/api/ossApi";

export default {
    namespaced: true,
    state: state,
    getters: {},
    mutations: {
        /** 设置当前路径 */
        setCurrentPath(state, path) {
            state.currentPath = path;
        },
        /** 设置当前路径列表 */
        setCurrentPathList(state, pathList) {
            state.currentPathList = pathList;
        },
        /** 设置上传对话框可见性 */
        setUploadDialogVisible(state, visible) {
            state.uploadDialogVisible = visible;
        },
        /** 设置文件列表 */
        setFileList(state, fileList) {
            state.fileList = fileList;
        },
        /** 设置上传表单 */
        setUploadForm(state, form) {
            state.uploadForm = form;
        },
        /** 设置查询参数 */
        setQueryParams(state, params) {
            state.queryParams = params;
        },
        /** 设置选中列表 */
        setSelectList(state, selectList) {
            state.selectList = selectList;
        },
        /** 设置表格数据 */
        setTableData(state, tableData) {
            state.tableData = tableData;
        },
        /** 设置总数 */
        setTotalCount(state, totalCount) {
            state.totalCount = totalCount;
        },
        /** 设置搜索可见性 */
        setShowSearch(state, showSearch) {
            state.showSearch = showSearch;
        }
    },
    actions: {
        /** 导航到指定路径 */
        navigateTo({commit, dispatch}, path) {
            // 设置当前路径
            commit('setCurrentPath', path);
            
            // 更新路径列表
            let pathList = [];
            if (path !== '/') {
                pathList = path.split('/').filter(item => item !== '');
            }
            commit('setCurrentPathList', pathList);
            
            // 重置页码
            commit('setQueryParams', {
                ...state.queryParams,
                currPage: 1
            });
            
            // 查询文件列表
            dispatch('handleQuery');
        },
        /** 返回上一级目录 */
        goBack({state, dispatch}) {
            if (state.currentPath === '/') return;
            
            // 获取上一级路径
            const pathParts = state.currentPath.split('/').filter(item => item !== '');
            pathParts.pop();
            const parentPath = pathParts.length > 0 ? '/' + pathParts.join('/') + '/' : '/';
            
            // 导航到上一级路径
            dispatch('navigateTo', parentPath);
        },
        /** 查询文件列表 */
         handleQuery({state, commit}) {
             // 确保查询参数中包含分隔符和当前路径前缀
             if (!state.queryParams.param) {
                 state.queryParams.param = {};
             }
               
             // 添加delimiter参数，恢复之前的正常行为
             state.queryParams.param.delimiter = '/';
             // 设置当前路径作为前缀，根目录时使用空字符串
             state.queryParams.param.prefix = state.currentPath === '/' ? '' : state.currentPath;
               
             console.log('发送的查询参数:', state.queryParams);
              
             getOssFilePage(state.queryParams).then(res => {
                 console.log('API返回完整响应:', res);
                 
                 if (res.code === 200) {
                     // 调试信息：打印API返回的原始数据
                     console.log('API返回的数据:', res.data);
                      
                     // 处理数据，确保目录和文件都能正确显示
                     let processedData = [];
                      
                     // 检查API返回的数据结构
                     console.log('API返回数据结构检查:');
                     console.log('- 是否包含directories数组:', res.data.directories && Array.isArray(res.data.directories));
                     console.log('- directories数组长度:', res.data.directories ? res.data.directories.length : 0);
                     console.log('- 是否包含files数组:', res.data.files && Array.isArray(res.data.files));
                     console.log('- files数组长度:', res.data.files ? res.data.files.length : 0);
                     console.log('- 是否包含list数组:', res.data.list && Array.isArray(res.data.list));
                     console.log('- list数组长度:', res.data.list ? res.data.list.length : 0);
                      
                     // 优先处理list数组（这是API返回的主要数据）
                     if (res.data.list && Array.isArray(res.data.list) && res.data.list.length > 0) {
                         console.log('开始处理list数组:');
                         processedData = res.data.list.map(item => {
                             // 支持key字段作为备选
                             const objectKey = item.objectKey || item.key;
                             
                             // 确保objectKey存在
                             if (!objectKey) {
                                 console.log('跳过没有objectKey或key的项目:', item);
                                 return null;
                             }
                             
                             // 调试信息：打印每个项目的完整数据
                             console.log('处理单个项目:', {
                                 item: item,
                                 objectKey: objectKey,
                                 hasIsDirectory: item.isDirectory !== undefined,
                                 itemIsDirectory: item.isDirectory,
                                 endsWithSlash: objectKey.endsWith('/')
                             });
                             
                             // 确保isDirectory字段被正确设置
                             // 优先使用item自带的isDirectory字段，如果不存在则根据objectKey是否以'/'结尾判断
                             const isDirectory = item.isDirectory !== undefined 
                                 ? item.isDirectory 
                                 : (objectKey.endsWith('/'));
                             
                             // 调试信息：打印判断结果
                             console.log('项目类型判断:', objectKey, '-> isDirectory:', isDirectory);
                             
                             // 确保所有必要字段都存在
                             const processedItem = {
                                 ...item,
                                 objectKey: objectKey,
                                 isDirectory: isDirectory,
                                 // 确保size字段存在（文件大小，目录可以为0）
                                 size: item.size !== undefined ? item.size : 0,
                                 // 确保lastModified字段存在
                                 lastModified: item.lastModified || item.lastModifiedDate || new Date().toISOString()
                             };
                             
                             return processedItem;
                         }).filter(Boolean); // 过滤掉null值
                     } 
                     // 如果没有list数组，尝试从directories和files数组获取数据
                     else {
                         console.log('没有list数组，尝试处理directories和files数组');
                         
                         // 处理directories数组
                         if (res.data.directories && Array.isArray(res.data.directories) && res.data.directories.length > 0) {
                             console.log('开始处理directories数组:');
                             const directories = res.data.directories.map(dir => {
                                 const objectKey = dir.objectKey || dir.key;
                                 return {
                                     ...dir,
                                     objectKey: objectKey,
                                     isDirectory: true,
                                     size: 0
                                 };
                             });
                             processedData = processedData.concat(directories);
                         }
                         
                         // 处理files数组
                         if (res.data.files && Array.isArray(res.data.files) && res.data.files.length > 0) {
                             console.log('开始处理files数组:');
                             const files = res.data.files.map(file => {
                                 const objectKey = file.objectKey || file.key;
                                 return {
                                     ...file,
                                     objectKey: objectKey,
                                     isDirectory: false
                                 };
                             });
                             processedData = processedData.concat(files);
                         }
                     }
                      
                     // 调试信息：打印处理后的数据
                     console.log('处理后的数据:', processedData);
                     console.log('处理后的数据数量:', processedData.length);
                     console.log('其中目录数量:', processedData.filter(item => item.isDirectory).length);
                     console.log('其中文件数量:', processedData.filter(item => !item.isDirectory).length);
                     
                     // 打印处理后的文件详情
                     console.log('处理后的文件详情:');
                     processedData.forEach((item, index) => {
                         console.log(`[${index}]`, {
                             name: item.objectKey,
                             isDirectory: item.isDirectory,
                             size: item.size,
                             lastModified: item.lastModified
                         });
                     });
                      
                     commit('setTableData', processedData);
                     // 更新总数
                     commit('setTotalCount', res.data.totalCount);
                 } else {
                     Message.error('获取文件列表失败');
                 }
             }).catch(err => {
                 console.error('获取文件列表失败：', err);
                 Message.error('获取文件列表失败');
             });
         },
         /** 重置查询 */
         resetQuery({state, commit, dispatch}) {
             commit('setQueryParams', {
                 ...state.queryParams,
                 param: {
                     objectKey: undefined,
                     prefix: state.currentPath
                     // 移除delimiter参数，避免只返回目录
                 }
             });
             dispatch('handleQuery');
         },
        /** 表格检查 */
        handleTableCheck({dispatch}) {
            dispatch('handleQuery');
        },
        /** 分页大小改变 */
        handleSizeChange({commit, dispatch}, val) {
            commit('setQueryParams', {
                ...state.queryParams,
                limit: val
            });
            dispatch('handleQuery');
        },
        /** 当前页改变 */
        handleCurrentChange({commit, dispatch}, val) {
            commit('setQueryParams', {
                ...state.queryParams,
                currPage: val
            });
            dispatch('handleQuery');
        },
        /** 选择变化 */
        handleSelectionChange({commit}, selection) {
            commit('setSelectList', selection);
        },
        /** 上传文件 */
        handleUpload({commit, state}) {
            commit('setUploadDialogVisible', true);
            commit('setUploadForm', {
                objectKey: state.currentPath
            });
            commit('setFileList', []);
        },
        /** 文件改变 */
        handleFileChange({commit, state}, fileList) {
            commit('setFileList', fileList);
            // 自动生成文件路径
            if (fileList && fileList.length > 0) {
                commit('setUploadForm', {
                    ...state.uploadForm,
                    objectKey: state.currentPath + fileList[0].name
                });
            }
        },
        /** 上传提交 */
        handleUploadSubmit({commit, state, dispatch}) {
            // 检查是否选择了文件
            if (!state.fileList || state.fileList.length === 0) {
                Message.warning('请先选择要上传的文件');
                return;
            }
            
            // 检查文件对象是否包含raw属性
            if (!state.fileList[0].raw) {
                console.error('文件对象缺少raw属性:', state.fileList[0]);
                Message.error('上传文件格式错误');
                return;
            }
            
            const formData = new FormData();
            formData.append('file', state.fileList[0].raw);
            formData.append('objectKey', state.uploadForm.objectKey);
            
            uploadOssFile(formData).then(res => {
                if (res.code === 200) {
                    Message.success('文件上传成功');
                    commit('setUploadDialogVisible', false);
                    dispatch('handleQuery');
                } else {
                    Message.error('文件上传失败');
                }
            }).catch(err => {
                console.error('文件上传失败：', err);
                Message.error('文件上传失败');
            });
        },
        /** 下载文件 */
        handleDownload({state}, row) {
            downloadOssFile(row.objectKey).then(res => {
                // 创建Blob对象
                const blob = new Blob([res.data]);
                // 创建下载链接
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                // 设置文件名
                a.download = row.objectKey.substring(row.objectKey.lastIndexOf('/') + 1);
                document.body.appendChild(a);
                a.click();
                // 清理
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                Message.success('文件下载成功');
            }).catch(err => {
                console.error('文件下载失败：', err);
                Message.error('文件下载失败');
            });
        },
        /** 复制文件 */
        handleCopy({dispatch}, row) {
            this._vm.$prompt('请输入新的文件路径', '复制文件', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                inputValue: row.objectKey + '.copy'
            }).then(({ value }) => {
                copyOssFile({
                    sourceObjectKey: row.objectKey,
                    targetObjectKey: value
                }).then(res => {
                    if (res.code === 200) {
                        Message.success('文件复制成功');
                        dispatch('handleQuery');
                    } else {
                        Message.error('文件复制失败');
                    }
                }).catch(err => {
                    console.error('文件复制失败：', err);
                    Message.error('文件复制失败');
                });
            }).catch(() => {
                Message.info('已取消复制');
            });
        },
        /** 删除文件 */
        handleDelete({dispatch}, row) {
            MessageBox.confirm(`此操作将永久删除 ${row.isDirectory ? '目录' : '文件'} ${row.objectKey}, 是否继续?`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                deleteOssFile(row.objectKey).then(res => {
                    if (res.code === 200) {
                        Message.success(`${row.isDirectory ? '目录' : '文件'} 删除成功`);
                        dispatch('handleQuery');
                    } else {
                        Message.error(`${row.isDirectory ? '目录' : '文件'} 删除失败`);
                    }
                }).catch(err => {
                    console.error(`${row.isDirectory ? '目录' : '文件'} 删除失败：`, err);
                    Message.error(`${row.isDirectory ? '目录' : '文件'} 删除失败`);
                });
            }).catch(() => {
                Message.info('已取消删除');
            });
        },
        /** 批量删除 */
        handleDeleteBatch({state, dispatch}, objectKeys) {
            if (!objectKeys) {
                objectKeys = state.selectList.map(item => item.objectKey);
            }
            
            if (objectKeys.length === 0) {
                Message.warning('请选择要删除的文件');
                return;
            }
            
            deleteBatchOssFile(objectKeys).then(res => {
                if (res.code === 200) {
                    Message.success('批量删除成功');
                    dispatch('handleQuery');
                } else {
                    Message.error('批量删除失败');
                }
            }).catch(err => {
                console.error('批量删除失败：', err);
                Message.error('批量删除失败');
            });
        },
        /** 删除全部 */
        handleDelAll({dispatch}) {
            dispatch('handleDeleteBatch');
        },
        /** 状态处理 */
        handleState() {
            // 可在此处添加状态处理逻辑
        }
    },
    modules: {}
}